import React, { useMemo, useContext } from "react";
import clsx from "clsx";

import { Context } from "../../../../../../teacherStore";
import {
  IActiveLearningIssue,
  IActiveLearningStudentAnswer,
} from "../../../../../../state";
import { getStudentAnswersByQuestion } from "../../../../../../libs/ActiveLearning";
import { LESSON5_ASSESSMENT_AXIS } from "../../../../../../constants/active_learning";
import AnswerStats from "../../../../../../atoms/ActiveLearning/AnswerStats";
import StudentAnswer from "../../../../../../molecules/ActiveLearning/StudentAnswer";
import styles from "../../../ActiveLearningIssue.module.scss";
import stylesAnswer from "../../../ActiveLearningAnswer.module.scss";

type Issue2AnswerProps = {
  materialCode: string;
  lessonNo: number;
  issue?: IActiveLearningIssue | undefined;
};

export const Issue2Answer = (props: Issue2AnswerProps) => {
  const { state } = useContext(Context);
  const currentIssueNo = 2;

  const question1 = useMemo(() => {
    return props.issue?.questions.find(q => q.order_num === 1);
  }, [props.issue?.questions]);

  const question2 = useMemo(() => {
    return props.issue?.questions.find(q => q.order_num === 2);
  }, [props.issue?.questions]);

  const question3 = useMemo(() => {
    return props.issue?.questions.find(q => q.order_num === 3);
  }, [props.issue?.questions]);

  const question4 = useMemo(() => {
    return props.issue?.questions.find(q => q.order_num === 4);
  }, [props.issue?.questions]);

  const question5 = useMemo(() => {
    return props.issue?.questions.find(q => q.order_num === 5);
  }, [props.issue?.questions]);

  const question6 = useMemo(() => {
    return props.issue?.questions.find(q => q.order_num === 6);
  }, [props.issue?.questions]);

  const question7 = useMemo(() => {
    return props.issue?.questions.find(q => q.order_num === 7);
  }, [props.issue?.questions]);

  const answersSortedByStudentName = useMemo(() => {
    return state.active_learning_answers?.sort((a, b) => {
      return a.last_name_kana.localeCompare(b.last_name_kana, "ja");
    });
  }, [state.active_learning_answers]);

  const studentAnswersI2 = useMemo(() => {
    return answersSortedByStudentName?.map(studentAnswers => {
      const filteredAnswersI1Q1 = getStudentAnswersByQuestion(
        props.materialCode,
        studentAnswers,
        props.lessonNo,
        1,
        1,
      );

      const filteredAnswersQ1 = getStudentAnswersByQuestion(
        props.materialCode,
        studentAnswers,
        props.lessonNo,
        currentIssueNo,
        1,
      );

      const filteredAnswersQ2 = getStudentAnswersByQuestion(
        props.materialCode,
        studentAnswers,
        props.lessonNo,
        currentIssueNo,
        2,
      );

      const filteredAnswersQ3 = getStudentAnswersByQuestion(
        props.materialCode,
        studentAnswers,
        props.lessonNo,
        currentIssueNo,
        3,
      );

      const filteredAnswersQ4 = getStudentAnswersByQuestion(
        props.materialCode,
        studentAnswers,
        props.lessonNo,
        currentIssueNo,
        4,
      );

      const filteredAnswersQ5 = getStudentAnswersByQuestion(
        props.materialCode,
        studentAnswers,
        props.lessonNo,
        currentIssueNo,
        5,
      );

      const allAnswers = [
        ...filteredAnswersI1Q1,
        ...filteredAnswersQ1,
        ...filteredAnswersQ2,
        ...filteredAnswersQ3,
        ...filteredAnswersQ4,
        ...filteredAnswersQ5,
      ];

      const uidMap = new Map();
      allAnswers?.forEach(answer => {
        const uid = answer.contents.uid || answer.contents.parentUid;
        if (uid) {
          if (!uidMap.has(uid)) {
            uidMap.set(uid, []);
          }
          uidMap.get(uid).push(answer);
        }
      });

      return {
        ...studentAnswers,
        answers: Array.from(uidMap.values()),
      };
    });
  }, [answersSortedByStudentName, props.materialCode, props.lessonNo]);

  const studentAnswerStatsI2 = useMemo(() => {
    const answered =
      studentAnswersI2?.filter(
        studentAnswers => studentAnswers.answers.length > 0,
      ).length || 0;
    const total = studentAnswersI2?.length || 0;
    const unanswered = total - answered;

    return {
      answered,
      total,
      unanswered,
    };
  }, [studentAnswersI2]);

  const studentAnswersQ6 = useMemo(() => {
    return answersSortedByStudentName?.map(studentAnswers => {
      const filteredAnswers = getStudentAnswersByQuestion(
        props.materialCode,
        studentAnswers,
        props.lessonNo,
        currentIssueNo,
        6,
      );

      return {
        ...studentAnswers,
        answers: filteredAnswers,
      };
    });
  }, [answersSortedByStudentName, props.materialCode, props.lessonNo]);

  const studentAnswerStatsQ6 = useMemo(() => {
    const answered =
      studentAnswersQ6?.filter(
        studentAnswers => studentAnswers.answers.length > 0,
      ).length || 0;
    const total = studentAnswersQ6?.length || 0;
    const unanswered = total - answered;

    return {
      answered,
      total,
      unanswered,
    };
  }, [studentAnswersQ6]);

  const studentAnswersQ7 = useMemo(() => {
    return answersSortedByStudentName?.map(studentAnswers => {
      const filteredAnswers = getStudentAnswersByQuestion(
        props.materialCode,
        studentAnswers,
        props.lessonNo,
        currentIssueNo,
        7,
      );

      return {
        ...studentAnswers,
        answers: filteredAnswers,
      };
    });
  }, [answersSortedByStudentName, props.materialCode, props.lessonNo]);

  const studentAnswerStatsQ7 = useMemo(() => {
    const answered =
      studentAnswersQ7?.filter(
        studentAnswers => studentAnswers.answers.length > 0,
      ).length || 0;
    const total = studentAnswersQ7?.length || 0;
    const unanswered = total - answered;

    return {
      answered,
      total,
      unanswered,
    };
  }, [studentAnswersQ7]);

  const getAssessmentAxisAnswersFrom = (
    answers: IActiveLearningStudentAnswer[],
  ) => {
    return answers.filter(
      (answer: IActiveLearningStudentAnswer) =>
        answer.issue_no === 2 &&
        answer.question_no >= 1 &&
        answer.question_no <= 4,
    );
  };

  const getResearchedAnswerFrom = (answers: IActiveLearningStudentAnswer[]) => {
    return answers.find(
      (answer: IActiveLearningStudentAnswer) => answer.question_no === 5,
    )?.contents?.value;
  };

  return (
    <div className={styles.issue}>
      <div className={clsx(styles.question, styles.primary)}>
        <div className={styles.header}>
          <span className={styles.no}>
            WORK{currentIssueNo < 10 ? `0${currentIssueNo}` : currentIssueNo}
          </span>
          <p className={styles.title}>{props.issue?.title}</p>
          <AnswerStats
            answered={studentAnswerStatsI2.answered}
            total={studentAnswerStatsI2.total}
            unanswered={studentAnswerStatsI2.unanswered}
          />
        </div>
        {question1 && question2 && question3 && question4 && question5 && (
          <div className={styles.answersArea}>
            {studentAnswersI2?.map((studentAnswers, index) => {
              const no = index + 1;
              const displayNo = no < 10 ? `0${no}` : `${no}`;
              const isAnswered = studentAnswers.answers.length > 0;
              return (
                <StudentAnswer
                  key={studentAnswers.student_id}
                  isAnswered={isAnswered}
                  isAccordion
                  no={displayNo}
                  name={`${studentAnswers.last_name} ${studentAnswers.first_name}`}
                  classId={studentAnswers.class_id}
                  studentId={studentAnswers.student_id}
                >
                  {isAnswered && (
                    <div className={styles.answersWrap}>
                      {studentAnswers.answers.map((answers, index) => (
                        <div key={index} className={stylesAnswer.tree}>
                          <p className={stylesAnswer.answer}>
                            {answers.map(
                              (answer: IActiveLearningStudentAnswer) =>
                                answer.question_no === 1 &&
                                answer.contents?.value,
                            )}
                          </p>
                          <ul className={stylesAnswer.branchList}>
                            <li className={stylesAnswer.branch}>
                              <div className={stylesAnswer.labelWrapper}>
                                <span className={stylesAnswer.label}>
                                  評価軸
                                </span>
                              </div>
                              <ul
                                className={clsx(
                                  stylesAnswer.answer,
                                  stylesAnswer.list,
                                )}
                              >
                                {getAssessmentAxisAnswersFrom(answers).length >
                                0
                                  ? getAssessmentAxisAnswersFrom(answers).map(
                                      (
                                        answer: IActiveLearningStudentAnswer,
                                      ) => (
                                        <li
                                          key={answer.id}
                                          className={styles.item}
                                        >
                                          {
                                            LESSON5_ASSESSMENT_AXIS[
                                              answer.question_no
                                            ]
                                          }
                                          →{answer.contents?.parentLabel}
                                        </li>
                                      ),
                                    )
                                  : "回答データがありません。"}
                              </ul>
                            </li>
                            <li className={stylesAnswer.branch}>
                              <div className={stylesAnswer.labelWrapper}>
                                <span className={stylesAnswer.label}>
                                  調べたこと
                                </span>
                              </div>
                              <p className={clsx(stylesAnswer.answer)}>
                                {getResearchedAnswerFrom(answers) ||
                                  "回答データがありません。"}
                              </p>
                            </li>
                          </ul>
                        </div>
                      ))}
                    </div>
                  )}
                </StudentAnswer>
              );
            })}
          </div>
        )}
      </div>
      {question6 && (
        <div className={clsx(styles.question, styles.secondary)}>
          <div className={styles.themEntry}>
            <p className={styles.title}>{question6.title}</p>
            <AnswerStats
              answered={studentAnswerStatsQ6.answered}
              total={studentAnswerStatsQ6.total}
              unanswered={studentAnswerStatsQ6.unanswered}
            />
          </div>
          <div className={styles.answersArea}>
            {studentAnswersQ6?.map((studentAnswers, index) => {
              const no = index + 1;
              const displayNo = no < 10 ? `0${no}` : `${no}`;
              const isAnswered = studentAnswers.answers.length > 0;
              return (
                <StudentAnswer
                  key={studentAnswers.student_id}
                  isAnswered={isAnswered}
                  no={displayNo}
                  name={`${studentAnswers.last_name} ${studentAnswers.first_name}`}
                  classId={studentAnswers.class_id}
                  studentId={studentAnswers.student_id}
                >
                  {isAnswered &&
                    studentAnswers.answers.map(answer => (
                      <p key={answer.id} className={stylesAnswer.answer}>
                        {answer.contents.value}
                      </p>
                    ))}
                </StudentAnswer>
              );
            })}
          </div>
        </div>
      )}
      {question7 && (
        <div className={clsx(styles.question, styles.secondary)}>
          <div className={styles.themEntry}>
            <p className={styles.title}>{question7.title}</p>
            <AnswerStats
              answered={studentAnswerStatsQ7.answered}
              total={studentAnswerStatsQ7.total}
              unanswered={studentAnswerStatsQ7.unanswered}
            />
          </div>
          <div className={styles.answersArea}>
            {studentAnswersQ7?.map((studentAnswers, index) => {
              const no = index + 1;
              const displayNo = no < 10 ? `0${no}` : `${no}`;
              const isAnswered = studentAnswers.answers.length > 0;
              return (
                <StudentAnswer
                  key={studentAnswers.student_id}
                  isAnswered={isAnswered}
                  no={displayNo}
                  name={`${studentAnswers.last_name} ${studentAnswers.first_name}`}
                  classId={studentAnswers.class_id}
                  studentId={studentAnswers.student_id}
                >
                  {isAnswered &&
                    studentAnswers.answers.map(answer => (
                      <p key={answer.id} className={stylesAnswer.answer}>
                        {answer.contents.value}
                      </p>
                    ))}
                </StudentAnswer>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
export default Issue2Answer;
